import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLoginModal } from '../context/LoginModalContext';
import { authAPI } from '../services/apiClient';
import '../styles/LoginModal.css';
import CountryCodePicker from './CountryCodePicker';

const PENDING_BOOKING_KEY = 'airfly_pending_booking';
const SKIP_VERIFY = process.env.REACT_APP_SKIP_EMAIL_VERIFICATION === 'true';

export default function LoginModal() {
  const { open, returnTo, message, initialTab, closeLoginModal } = useLoginModal();
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const firstFieldRef = useRef(null);

  // ── tab: 'signin' | 'register' | 'phone' ──────────────────────────────────
  const [tab, setTab] = useState('signin');
  // phone sub-step: 'number' | 'code'
  const [phoneStep, setPhoneStep] = useState('number');
  const [successMsg, setSuccessMsg] = useState('');

  // ── Sign-in state ──────────────────────────────────────────────────────────
  const [signin, setSignin] = useState({ email: '', password: '', remember: false });
  const [signinErrors, setSigninErrors] = useState({ email: '', password: '' });
  const [signinApiError, setSigninApiError] = useState('');
  const [signinLoading, setSigninLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendSent, setResendSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  // ── Register state ────────────────────────────────────────────────────────
  const [reg, setReg] = useState({ fullName: '', email: '', password: '', confirmPassword: '', referralCode: '', agreeToTerms: false });
  const [regErrors, setRegErrors] = useState({});
  const [regApiError, setRegApiError] = useState('');
  const [regLoading, setRegLoading] = useState(false);

  // ── Phone OTP state ───────────────────────────────────────────────────────
  const [dialCode, setDialCode] = useState('+61');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sentPhone, setSentPhone] = useState(''); // E.164 number that OTP was sent to
  const [phoneError, setPhoneError] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);

  // ── Phone profile completion (new users after OTP) ────────────────────────
  const [profile, setProfile] = useState({ fullName: '', email: '' });
  const [profileErrors, setProfileErrors] = useState({});
  const [profileLoading, setProfileLoading] = useState(false);

  // ── Forgot password state ─────────────────────────────────────────────────
  const [fpEmail, setFpEmail] = useState('');
  const [fpError, setFpError] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpSent, setFpSent] = useState(false);

  // ── Reset on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setTab(initialTab || 'signin');
      setPhoneStep('number');
      setSuccessMsg('');
      setSignin({ email: '', password: '', remember: false });
      setSigninErrors({ email: '', password: '' });
      setSigninApiError('');
      setUnverifiedEmail('');
      setResendSent(false);
      
      // Extract referral code from URL if present
      const urlParams = new URLSearchParams(window.location.search);
      const refFromUrl = urlParams.get('ref') || '';
      
      setReg({ fullName: '', email: '', password: '', confirmPassword: '', referralCode: refFromUrl, agreeToTerms: false });
      setRegErrors({});
      setRegApiError('');
      setDialCode('+61');
      setPhoneNumber('');
      setSentPhone('');
      setPhoneError('');
      setOtp('');
      setOtpError('');
      setProfile({ fullName: '', email: '' });
      setProfileErrors({});
      setFpEmail('');
      setFpError('');
      setFpLoading(false);
      setFpSent(false);
      setTimeout(() => firstFieldRef.current?.focus(), 80);
    }
  }, [open, initialTab]);

  // ── Keyboard + scroll lock ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') closeLoginModal(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, closeLoginModal]);

  // ── Post-login navigation ──────────────────────────────────────────────────
  const handlePostLogin = useCallback(() => {
    closeLoginModal();
    if (returnTo === '/flight-details') {
      try {
        const pending = JSON.parse(sessionStorage.getItem(PENDING_BOOKING_KEY));
        if (pending?.flight) { sessionStorage.removeItem(PENDING_BOOKING_KEY); navigate('/flight-details', { state: pending }); return; }
      } catch { /* fall through */ }
    }
    if (returnTo === '/booking-preview') { navigate('/booking-preview'); return; }
    try {
      const pending = JSON.parse(sessionStorage.getItem('airfly_pending_round_trip'));
      if (pending?.flight) { sessionStorage.removeItem('airfly_pending_round_trip'); navigate('/flight-details', { state: pending }); return; }
    } catch { /* fall through */ }
    if (returnTo) navigate(returnTo);
  }, [closeLoginModal, navigate, returnTo]);

  // ── Sign-in submit ─────────────────────────────────────────────────────────
  const handleSignin = (e) => {
    e.preventDefault();
    const errs = { email: '', password: '' };
    if (!signin.email.trim()) errs.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signin.email.trim())) errs.email = 'Enter a valid email address.';
    if (!signin.password.trim()) errs.password = 'Password is required.';
    setSigninErrors(errs);
    if (errs.email || errs.password) return;

    setSigninApiError('');
    setSigninLoading(true);
    const t = setTimeout(() => { setSigninLoading(false); setSigninApiError('Login timed out. Please try again.'); }, 15000);
    login(signin.email, signin.password, signin.remember)
      .then(() => { clearTimeout(t); handlePostLogin(); })
      .catch((err) => {
        clearTimeout(t);
        if (err.code === 'EMAIL_NOT_VERIFIED') { setUnverifiedEmail(signin.email); setSigninApiError(''); }
        else setSigninApiError(err.message || 'Login failed. Please try again.');
      })
      .finally(() => setSigninLoading(false));
  };

  const handleResend = () => {
    if (!unverifiedEmail || resendLoading) return;
    setResendLoading(true);
    authAPI.resendVerification(unverifiedEmail)
      .then(() => setResendSent(true))
      .catch(() => setResendSent(true))
      .finally(() => setResendLoading(false));
  };

  // ── Register submit ────────────────────────────────────────────────────────
  const handleRegister = (e) => {
    e.preventDefault();
    const errs = {};
    if (!reg.fullName.trim()) errs.fullName = 'Full name is required.';
    if (!reg.email.trim()) errs.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reg.email.trim())) errs.email = 'Enter a valid email.';
    if (!reg.password) errs.password = 'Password is required.';
    else if (reg.password.length < 8) errs.password = 'At least 8 characters.';
    else if (!/[A-Z]/.test(reg.password)) errs.password = 'Include an uppercase letter.';
    else if (!/[a-z]/.test(reg.password)) errs.password = 'Include a lowercase letter.';
    else if (!/[0-9]/.test(reg.password)) errs.password = 'Include a number.';
    if (reg.confirmPassword !== reg.password) errs.confirmPassword = 'Passwords do not match.';
    // Referral code is optional but validate format if provided
    if (reg.referralCode.trim() && !/^[A-Z0-9]{8,12}$/i.test(reg.referralCode.trim())) {
      errs.referralCode = 'Referral code must be 8-12 alphanumeric characters.';
    }
    if (!reg.agreeToTerms) errs.agreeToTerms = 'You must agree to the terms.';
    setRegErrors(errs);
    if (Object.keys(errs).length) return;

    setRegApiError('');
    setRegLoading(true);
    authAPI.register(reg.fullName.trim(), reg.email.trim(), reg.password, reg.referralCode.trim() || null)
      .then(() => {
        if (SKIP_VERIFY) {
          return login(reg.email.trim(), reg.password).then(() => handlePostLogin());
        }
        setSuccessMsg('Account created! Check your email for a verification link, then sign in.');
        setTab('signin');
      })
      .catch((err) => setRegApiError(err.message || 'Registration failed. Please try again.'))
      .finally(() => setRegLoading(false));
  };

  // ── Phone OTP ──────────────────────────────────────────────────────────────
  const handleSendOTP = (e) => {
    e.preventDefault();
    const digits = phoneNumber.replace(/\D/g, '');
    if (!digits || digits.length < 6 || digits.length > 14) {
      setPhoneError('Enter a valid mobile number.');
      return;
    }
    // Build E.164: strip leading zero if present, then prepend dial code
    const e164 = dialCode + (digits.startsWith('0') ? digits.slice(1) : digits);
    setPhoneError('');
    setPhoneLoading(true);
    authAPI.sendPhoneOTP(e164)
      .then(() => { setSentPhone(e164); setPhoneStep('code'); })
      .catch((err) => setPhoneError(err.message || 'Could not send OTP. Please try again.'))
      .finally(() => setPhoneLoading(false));
  };

  const handleVerifyOTP = (e) => {
    e.preventDefault();
    if (!otp.trim() || otp.trim().length < 4) { setOtpError('Enter the code we sent you.'); return; }
    setOtpError('');
    setPhoneLoading(true);
    authAPI.verifyPhoneOTP(sentPhone, otp.trim())
      .then((data) => {
        if (data.isNewUser) {
          // New phone user — collect name + email before finishing sign-in
          setPhoneStep('profile');
        } else {
          setUser(data.user || authAPI.getUser());
          handlePostLogin();
        }
      })
      .catch((err) => setOtpError(err.message || 'Invalid or expired code.'))
      .finally(() => setPhoneLoading(false));
  };

  const handleCompleteProfile = (e) => {
    e.preventDefault();
    const errs = {};
    if (!profile.fullName.trim()) errs.fullName = 'Full name is required.';
    if (profile.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email.trim())) {
      errs.email = 'Enter a valid email address.';
    }
    setProfileErrors(errs);
    if (Object.keys(errs).length) return;

    setProfileLoading(true);
    authAPI.completePhoneProfile(sentPhone, profile.fullName.trim(), profile.email.trim())
      .then((data) => { setUser(data.user || authAPI.getUser()); handlePostLogin(); })
      .catch((err) => setProfileErrors({ api: err.message || 'Could not save profile. Please try again.' }))
      .finally(() => setProfileLoading(false));
  };

  // ── Forgot password ───────────────────────────────────────────────────────
  const handleForgot = (e) => {
    e.preventDefault();
    if (!fpEmail.trim()) { setFpError('Email is required.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fpEmail.trim())) { setFpError('Enter a valid email address.'); return; }
    setFpError('');
    setFpLoading(true);
    authAPI.forgotPassword(fpEmail.trim())
      // Always show success (don't reveal whether email exists)
      .then(() => setFpSent(true))
      .catch(() => setFpSent(true))
      .finally(() => setFpLoading(false));
  };

  if (!open) return null;

  return (
    <div
      className="lm-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Sign in to AirFlyNow"
      onClick={(e) => { if (e.target === e.currentTarget) closeLoginModal(); }}
    >
      <div className="lm-card">
        {/* ── Header ── */}
        <div className="lm-header">
          <img src="/branding/logo-display.png" alt="AirFlyNow" className="lm-header__logo" />
          <button type="button" className="lm-close" aria-label="Close" onClick={closeLoginModal}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── Tab switcher ── */}
        {tab !== 'phone' && tab !== 'forgot' && (
          <div className="lm-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={tab === 'signin'}
              className={`lm-tab${tab === 'signin' ? ' lm-tab--active' : ''}`}
              onClick={() => { setTab('signin'); setSuccessMsg(''); }}
            >Sign in</button>
            <button
              role="tab"
              aria-selected={tab === 'register'}
              className={`lm-tab${tab === 'register' ? ' lm-tab--active' : ''}`}
              onClick={() => { setTab('register'); setSuccessMsg(''); }}
            >Create account</button>
          </div>
        )}

        {/* ── Success banner ── */}
        {successMsg && <div className="lm-banner lm-banner--success">{successMsg}</div>}
        {message && !successMsg && <div className="lm-banner lm-banner--info">{message}</div>}

        {/* ══ SIGN IN TAB ═══════════════════════════════════════════════════ */}
        {tab === 'signin' && (
          <form className="lm-form" onSubmit={handleSignin} noValidate>
            {signinApiError && <div className="lm-banner lm-banner--error" role="alert">{signinApiError}</div>}
            {unverifiedEmail && !resendSent && (
              <div className="lm-banner lm-banner--warn" role="alert">
                ✉️ Please verify your email first.{' '}
                <button type="button" className="lm-text-btn" onClick={handleResend} disabled={resendLoading}>
                  {resendLoading ? 'Sending…' : 'Resend email'}
                </button>
              </div>
            )}
            {resendSent && <div className="lm-banner lm-banner--success">✅ Verification email sent! Check your inbox.</div>}

            <div className="lm-field">
              <label className="lm-label" htmlFor="lm-email">Email</label>
              <input
                ref={firstFieldRef}
                id="lm-email"
                className={`lm-input${signinErrors.email ? ' lm-input--error' : ''}`}
                type="email" name="email" autoComplete="email"
                value={signin.email}
                onChange={(e) => setSignin((p) => ({ ...p, email: e.target.value }))}
                placeholder="you@example.com"
                disabled={signinLoading}
                required
              />
              {signinErrors.email && <p className="lm-error">{signinErrors.email}</p>}
            </div>

            <div className="lm-field">
              <div className="lm-label-row">
                <label className="lm-label" htmlFor="lm-password">Password</label>
                <button
                  type="button"
                  className="lm-forgot"
                  onClick={() => { setFpEmail(signin.email); setFpError(''); setFpSent(false); setTab('forgot'); }}
                >Forgot?</button>
              </div>
              <input
                id="lm-password"
                className={`lm-input${signinErrors.password ? ' lm-input--error' : ''}`}
                type="password" name="password" autoComplete="current-password"
                value={signin.password}
                onChange={(e) => setSignin((p) => ({ ...p, password: e.target.value }))}
                placeholder="Your password"
                disabled={signinLoading}
              />
              {signinErrors.password && <p className="lm-error">{signinErrors.password}</p>}
            </div>

            <label className="lm-remember">
              <input type="checkbox" checked={signin.remember} onChange={(e) => setSignin((p) => ({ ...p, remember: e.target.checked }))} />
              Remember me
            </label>

            <button className="lm-submit" type="submit" disabled={signinLoading}>
              {signinLoading ? 'Signing in…' : 'Sign in'}
            </button>

            {/* TEMPORARILY DISABLED: Phone authentication not working */}
            {/* <div className="lm-divider"><span>or</span></div>

            <button type="button" className="lm-phone-btn" onClick={() => setTab('phone')}>
              <span style={{ fontSize: '18px', marginRight: '8px' }}>📱</span>
              Continue with phone number
            </button> */}
          </form>
        )}

        {/* ══ REGISTER TAB ══════════════════════════════════════════════════ */}
        {tab === 'register' && (
          <form className="lm-form" onSubmit={handleRegister} noValidate>
            {regApiError && <div className="lm-banner lm-banner--error" role="alert">{regApiError}</div>}

            <div className="lm-field">
              <label className="lm-label" htmlFor="lm-reg-name">Full name</label>
              <input
                ref={firstFieldRef}
                id="lm-reg-name"
                className={`lm-input${regErrors.fullName ? ' lm-input--error' : ''}`}
                type="text" autoComplete="name"
                value={reg.fullName}
                onChange={(e) => setReg((p) => ({ ...p, fullName: e.target.value }))}
                placeholder="Jane Doe"
                disabled={regLoading}
              />
              {regErrors.fullName && <p className="lm-error">{regErrors.fullName}</p>}
            </div>

            <div className="lm-field">
              <label className="lm-label" htmlFor="lm-reg-email">Email</label>
              <input
                id="lm-reg-email"
                className={`lm-input${regErrors.email ? ' lm-input--error' : ''}`}
                type="email" autoComplete="email"
                value={reg.email}
                onChange={(e) => setReg((p) => ({ ...p, email: e.target.value }))}
                placeholder="you@example.com"
                disabled={regLoading}
                required
              />
              {regErrors.email && <p className="lm-error">{regErrors.email}</p>}
            </div>

            <div className="lm-field">
              <label className="lm-label" htmlFor="lm-reg-pw">Password</label>
              <input
                id="lm-reg-pw"
                className={`lm-input${regErrors.password ? ' lm-input--error' : ''}`}
                type="password" autoComplete="new-password"
                value={reg.password}
                onChange={(e) => setReg((p) => ({ ...p, password: e.target.value }))}
                placeholder="Min 8 chars, uppercase, number"
                disabled={regLoading}
              />
              {regErrors.password && <p className="lm-error">{regErrors.password}</p>}
            </div>

            <div className="lm-field">
              <label className="lm-label" htmlFor="lm-reg-cpw">Confirm password</label>
              <input
                id="lm-reg-cpw"
                className={`lm-input${regErrors.confirmPassword ? ' lm-input--error' : ''}`}
                type="password" autoComplete="new-password"
                value={reg.confirmPassword}
                onChange={(e) => setReg((p) => ({ ...p, confirmPassword: e.target.value }))}
                placeholder="Repeat password"
                disabled={regLoading}
              />
              {regErrors.confirmPassword && <p className="lm-error">{regErrors.confirmPassword}</p>}
            </div>

            <div className="lm-field">
              <label className="lm-label" htmlFor="lm-reg-referral">
                Referral Code <span style={{ fontWeight: 400, color: '#64748b', fontSize: '13px' }}>(Optional)</span>
              </label>
              <input
                id="lm-reg-referral"
                className={`lm-input${regErrors.referralCode ? ' lm-input--error' : ''}`}
                type="text" autoComplete="off"
                value={reg.referralCode}
                onChange={(e) => setReg((p) => ({ ...p, referralCode: e.target.value }))}
                placeholder="Enter friend's referral code"
                disabled={regLoading}
              />
              {regErrors.referralCode ? (
                <p className="lm-error">{regErrors.referralCode}</p>
              ) : (
                <p className="lm-helper-text" style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                  💎 You'll both get 1,000 points ($10 off) after your first booking!
                </p>
              )}
            </div>

            <label className="lm-remember lm-terms">
              <input
                type="checkbox"
                checked={reg.agreeToTerms}
                onChange={(e) => setReg((p) => ({ ...p, agreeToTerms: e.target.checked }))}
              />
              <span>
                I agree to the{' '}
                <Link to="/terms-of-use" className="lm-forgot" onClick={closeLoginModal}>Terms of Use</Link>
                {' '}and{' '}
                <Link to="/privacy-policy" className="lm-forgot" onClick={closeLoginModal}>Privacy Policy</Link>
              </span>
            </label>
            {regErrors.agreeToTerms && <p className="lm-error" style={{marginTop: -8}}>{regErrors.agreeToTerms}</p>}

            <button className="lm-submit" type="submit" disabled={regLoading}>
              {regLoading ? 'Creating account…' : 'Create account'}
            </button>

            {/* TEMPORARILY DISABLED: Phone authentication not working */}
            {/* <div className="lm-divider"><span>or</span></div>

            <button type="button" className="lm-phone-btn" onClick={() => setTab('phone')}>
              <span style={{ fontSize: '18px', marginRight: '8px' }}>📱</span>
              Continue with phone number
            </button> */}
          </form>
        )}

        {/* ══ FORGOT PASSWORD TAB ════════════════════════════════════════════════ */}
        {tab === 'forgot' && (
          <div className="lm-phone-panel">
            <button type="button" className="lm-back-btn" onClick={() => setTab('signin')}>
              <span style={{ fontSize: '16px', marginRight: '4px' }}>←</span>
              Back to sign in
            </button>
            <div className="lm-phone-icon" aria-hidden="true" style={{ fontSize: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ✉️
            </div>
            <h3 className="lm-phone-title">Reset your password</h3>
            {!fpSent ? (
              <>
                <p className="lm-phone-sub">Enter your account email and we’ll send you a reset link.</p>
                <form className="lm-form" onSubmit={handleForgot} noValidate>
                  <div className="lm-field">
                    <label className="lm-label" htmlFor="lm-fp-email">Email</label>
                    <input
                      ref={firstFieldRef}
                      id="lm-fp-email"
                      className={`lm-input${fpError ? ' lm-input--error' : ''}`}
                      type="email" autoComplete="email"
                      value={fpEmail}
                      onChange={(e) => { setFpEmail(e.target.value); setFpError(''); }}
                      placeholder="you@example.com"
                      disabled={fpLoading}
                      required
                    />
                    {fpError && <p className="lm-error">{fpError}</p>}
                  </div>
                  <button className="lm-submit" type="submit" disabled={fpLoading}>
                    {fpLoading ? 'Sending…' : 'Send reset link'}
                  </button>
                </form>
              </>
            ) : (
              <div className="lm-fp-success">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12l3 3 5-5"/></svg>
                <p className="lm-fp-success-title">Check your inbox</p>
                <p className="lm-fp-success-sub">If <strong>{fpEmail}</strong> matches an account, we’ll send a reset link. It expires in 1 hour.</p>
                <button type="button" className="lm-submit" style={{marginTop: 8}} onClick={() => setTab('signin')}>
                  Back to sign in
                </button>
              </div>
            )}
          </div>
        )}

        {/* ══ PHONE OTP TAB ══════════════════════════════════════════════════ */}
        {tab === 'phone' && (
          <div className="lm-phone-panel">
            <button type="button" className="lm-back-btn" onClick={() => {
              setTab('signin'); setPhoneStep('number');
              setDialCode('+61'); setPhoneNumber(''); setSentPhone('');
              setOtp(''); setPhoneError(''); setOtpError('');
              setProfile({ fullName: '', email: '' }); setProfileErrors({});
            }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M10 3L5 8l5 5"/></svg>
              Back
            </button>
            <div className="lm-phone-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0066ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><circle cx="12" cy="17" r="1"/></svg>
            </div>
            <h3 className="lm-phone-title">
              {phoneStep === 'number' && 'Enter your mobile number'}
              {phoneStep === 'code' && 'Enter verification code'}
              {phoneStep === 'profile' && 'Almost there!'}
            </h3>
            <p className="lm-phone-sub">
              {phoneStep === 'number' && "We'll send a one-time code via SMS and WhatsApp."}
              {phoneStep === 'code' && `Code sent to ${sentPhone} via SMS and WhatsApp. Use whichever you receive.`}
              {phoneStep === 'profile' && 'Tell us your name so we can personalise your experience.'}
            </p>

            {phoneStep === 'number' ? (
              <form className="lm-form" onSubmit={handleSendOTP} noValidate>
                <div className="lm-field">
                  <label className="lm-label" htmlFor="lm-phone">Mobile number</label>
                  <div className="lm-phone-input-row">
                    <CountryCodePicker
                      value={dialCode}
                      onChange={setDialCode}
                      disabled={phoneLoading}
                      id="lm-dial-code"
                    />
                    <input
                      ref={firstFieldRef}
                      id="lm-phone"
                      className={`lm-input${phoneError ? ' lm-input--error' : ''}`}
                      type="tel" autoComplete="tel-national"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="412 345 678"
                      disabled={phoneLoading}
                    />
                  </div>
                  {phoneError && <p className="lm-error">{phoneError}</p>}
                </div>
                <button className="lm-submit" type="submit" disabled={phoneLoading}>
                  {phoneLoading ? 'Sending…' : 'Send code'}
                </button>
              </form>
            ) : (
              <form className="lm-form" onSubmit={handleVerifyOTP} noValidate>
                <div className="lm-field">
                  <label className="lm-label" htmlFor="lm-otp">Verification code</label>
                  <input
                    id="lm-otp"
                    className={`lm-input lm-input--otp${otpError ? ' lm-input--error' : ''}`}
                    type="text" inputMode="numeric" autoComplete="one-time-code"
                    maxLength={8}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    disabled={phoneLoading}
                  />
                  {otpError && <p className="lm-error">{otpError}</p>}
                </div>
                <p className="lm-otp-hint">Check SMS or WhatsApp for the code.</p>
                <button className="lm-submit" type="submit" disabled={phoneLoading}>
                  {phoneLoading ? 'Verifying…' : 'Verify & sign in'}
                </button>
                <button type="button" className="lm-text-link" onClick={() => { setPhoneStep('number'); setOtp(''); setOtpError(''); }}>
                  Didn’t receive a code? Try again
                </button>
              </form>
            )}

            {/* ── Profile completion step (new users only) ── */}
            {phoneStep === 'profile' && (
              <form className="lm-form" onSubmit={handleCompleteProfile} noValidate>
                {profileErrors.api && <div className="lm-banner lm-banner--error" role="alert">{profileErrors.api}</div>}
                <div className="lm-field">
                  <label className="lm-label" htmlFor="lm-prof-name">Full name <span className="lm-required">*</span></label>
                  <input
                    ref={firstFieldRef}
                    id="lm-prof-name"
                    className={`lm-input${profileErrors.fullName ? ' lm-input--error' : ''}`}
                    type="text" autoComplete="name"
                    value={profile.fullName}
                    onChange={(e) => setProfile((p) => ({ ...p, fullName: e.target.value }))}
                    placeholder="Jane Doe"
                    disabled={profileLoading}
                  />
                  {profileErrors.fullName && <p className="lm-error">{profileErrors.fullName}</p>}
                </div>
                <div className="lm-field">
                  <label className="lm-label" htmlFor="lm-prof-email">
                    Email <span className="lm-optional">(optional — for booking confirmations)</span>
                  </label>
                  <input
                    id="lm-prof-email"
                    className={`lm-input${profileErrors.email ? ' lm-input--error' : ''}`}
                    type="email" autoComplete="email"
                    value={profile.email}
                    onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                    placeholder="you@example.com"
                    disabled={profileLoading}
                  />
                  {profileErrors.email && <p className="lm-error">{profileErrors.email}</p>}
                </div>
                <div className="lm-profile-phone-row">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0066ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="2" width="14" height="20" rx="2"/><circle cx="12" cy="17" r="1"/></svg>
                  <span>Verified: {sentPhone}</span>
                </div>
                <button className="lm-submit" type="submit" disabled={profileLoading}>
                  {profileLoading ? 'Saving…' : 'Complete sign-up'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* ── Footer ── */}
        {tab !== 'phone' && tab !== 'forgot' && (
          <p className="lm-footer">
            {tab === 'signin'
              ? <>No account? <button type="button" className="lm-footer__link" onClick={() => setTab('register')}>Create one free →</button></>
              : <>Already have an account? <button type="button" className="lm-footer__link" onClick={() => setTab('signin')}>Sign in →</button></>
            }
          </p>
        )}
      </div>
    </div>
  );
}
